import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Image, Zap } from 'lucide-react';

export const PortfolioPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const projects = [
    {
      id: 1,
      title: 'Adega Project',
      category: 'real-estate',
      client: 'Pentagon Prestige LDA',
      description: 'Comprehensive aerial photography and surveying for residential development in Palmela-Setubal district',
      image: '/assets/portfolio-portugal-1.jpg',
      imageAlt: 'Aerial view of Adega residential development project in Portugal',
      stats: { 'land area': '42,000 m²', 'residential units': '54 units', location: 'Palmela, Setubal, Portugal' }
    },
    {
      id: 2,
      title: 'St. Peter\'s School Project',
      category: 'inspection',
      client: 'TS Holding',
      description: 'Comprehensive structural inspection and assessment of school campus buildings',
      image: '/assets/portfolio-infrastructure-1.jpg',
      imageAlt: 'Structural inspection of St. Peter\'s School campus buildings',
      stats: { resolution: 'Ultra HD', coverage: 'Full campus', assessment: 'Structural analysis complete' }
    },
    {
      id: 3,
      title: 'Agricultural Land Mapping',
      category: 'mapping',
      headline: 'Measurements & Surveying',
      client: 'Government of Rwanda',
      image: '/assets/portfolio-mapping-1.mp4',
      imageAlt: 'Agricultural land mapping and surveying for precision farming',
      stats: {}
    },
    {
      id: 4,
      title: 'Solar Installation Survey',
      category: 'thermal',
      headline: 'Infrastructure Inspection',
      client: 'Thermal & Analysis',
      description: 'Thermal and visual inspection of solar installation',
      image: '/assets/portfolio-thermal-1.jpg',
      imageAlt: 'Thermal inspection of solar installation',
      stats: { panels: '48,000 units', defects: 'Identified 23 issues', time: '2 days onsite' }
    },
    {
      id: 5,
      title: 'Custom Project',
      category: 'custom',
      client: 'Custom Project',
      image: '/assets/portfolio-custom-1.jpg',
      imageAlt: 'Custom project',
      stats: {}
    },
  ];

  const categories = [
    { id: 'all', label: 'All Projects' },
    { id: 'real-estate', label: 'Real Estate' },
    { id: 'inspection', label: 'Infrastructure' },
    { id: 'mapping', label: 'Mapping & Surveying' },
    { id: 'thermal', label: 'Thermal & Analysis' },
    { id: 'custom', label: 'Custom' },
  ];

  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(p => p.category === selectedCategory);

  return (
    <main className="min-h-screen bg-white text-zinc-900 overflow-x-hidden">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-96 flex items-center justify-center px-6 py-32 mt-16 bg-gradient-to-br from-blue-600 to-cyan-500">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.h1
            className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Our Portfolio
          </motion.h1>
          <motion.p
            className="text-xl text-gray-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Showcase of completed projects across multiple industries
          </motion.p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 px-6 bg-gray-50 sticky top-24 z-40">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-wrap gap-4 justify-center">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white shadow-lg scale-105'
                    : 'bg-white text-zinc-700 border border-gray-300 hover:border-blue-300'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category.label}
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            layout
          >
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                layout
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                {/* Project Image */}
                <div className="h-48 relative overflow-hidden bg-gray-200">
                  {project.image.startsWith('/') ? (
                    project.image.endsWith('.mp4') ? (
                      <video
                        src={project.image}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        muted
                        loop
                        autoPlay
                      />
                    ) : (
                      <img
                        src={project.image}
                        alt={project.imageAlt || project.title}
                        className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ${project.id === 5 ? '-rotate-90' : ''}`}
                      />
                    )
                  ) : (
                    <div className={`bg-gradient-to-br ${project.image} h-full flex items-center justify-center group-hover:scale-110 transition-transform duration-500`}>
                      <Image className="w-16 h-16 text-white/30" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300"></div>
                </div>

                {/* Project Content */}
                <div className="p-6 bg-white">
                  {project.headline && (
                    <h3 className="text-lg font-bold text-zinc-900 mb-3">{project.headline}</h3>
                  )}
                  <p className="text-sm text-zinc-600">{project.client}</p>

                  {/* Stats */}
                  {Object.keys(project.stats).length > 0 && (
                    <div className="space-y-2 text-sm text-zinc-600 border-t pt-4 mt-4">
                      {Object.entries(project.stats).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="capitalize">{key}:</span>
                          <span className="font-semibold text-zinc-900">{value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Empty State */}
          {filteredProjects.length === 0 && (
            <motion.div
              className="text-center py-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <p className="text-xl text-zinc-600">No projects in this category yet.</p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            className="grid md:grid-cols-4 gap-8 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {[
              { number: '100+', label: 'Projects Completed' },
              { number: '80+', label: 'Satisfied Clients' },
              { number: '15,000+', label: 'Flight Hours' },
              { number: '99.8%', label: 'Success Rate' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-zinc-700">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-blue-600">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Your Project Awaits</h2>
            <p className="text-xl text-gray-100 mb-8">
              Join hundreds of satisfied clients who have transformed their operations with our drone services.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 hover:scale-105 active:scale-95 transition-all duration-200"
            >
              Start Your Project
              <Zap className="w-5 h-5 ml-2" />
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
};
